import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { answerCall, endCall, getActive } from '../lib/rtc';
import { useStore } from '../lib/store';
import { wsClient } from '../lib/ws';

function initials(name: string): string {
  return name.split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase() ?? '').join('') || '?';
}
const TONES = ['clay', 'water', 'sage', 'plum', 'dune'] as const;
function toneFor(id: string) {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
  return TONES[Math.abs(h) % TONES.length];
}

function MicOnIcon()  { return (<svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="3" width="6" height="12" rx="3" fill="currentColor" stroke="none"/><path d="M5 11a7 7 0 0 0 14 0M12 18v3M8 21h8" /></svg>); }
function MicOffIcon() { return (<svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3l18 18"/><path d="M9 9v3a3 3 0 0 0 5.1 2.1M15 9V6a3 3 0 0 0-6 0"/><path d="M17 11a5 5 0 0 1-.9 2.9M5 11a7 7 0 0 0 12 4.9M12 18v3M8 21h8"/></svg>); }
function CamOnIcon()  { return (<svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"><path d="M2 8a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2z" fill="currentColor" stroke="none"/><path d="M16 10l6-4v12l-6-4z" fill="currentColor" stroke="none"/></svg>); }
function CamOffIcon() { return (<svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3l18 18"/><path d="M22 6v12l-6-4V10z"/><path d="M14 6H4a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h10"/></svg>); }
function SpeakerIcon() { return (<svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 5L6 9H2v6h4l5 4z" fill="currentColor" stroke="none"/><path d="M15 9a4 4 0 0 1 0 6M18 6a8 8 0 0 1 0 12"/></svg>); }
function HangupIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <g transform="rotate(135 12 12)">
        <path d="M22 16.9v3a2 2 0 0 1-2.2 2 20 20 0 0 1-8.6-3.1 20 20 0 0 1-6.2-6.1A20 20 0 0 1 2 4.2 2 2 0 0 1 4 2h3a2 2 0 0 1 2 1.7c.1.9.3 1.7.6 2.5a2 2 0 0 1-.5 2.1L7.9 9.4a16 16 0 0 0 6 6l1.1-1.2a2 2 0 0 1 2.1-.4c.8.3 1.6.5 2.5.6A2 2 0 0 1 22 16.9z" fill="currentColor" stroke="none"/>
      </g>
    </svg>
  );
}
function SignalIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
      <rect x="2"  y="14" width="4" height="6" rx="1"/>
      <rect x="8"  y="10" width="4" height="10" rx="1"/>
      <rect x="14" y="6"  width="4" height="14" rx="1"/>
      <rect x="20" y="2"  width="4" height="18" rx="1" opacity="0.4"/>
    </svg>
  );
}
function MinimizeIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
      <path d="M5 12h14"/>
    </svg>
  );
}

function useElapsed(startAt: number | null): string {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!startAt) return;
    const t = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(t);
  }, [startAt]);
  if (!startAt) return '';
  const s = Math.max(0, Math.floor((now - startAt) / 1000));
  const mm = Math.floor(s / 60).toString().padStart(2, '0');
  const ss = (s % 60).toString().padStart(2, '0');
  return `${mm}:${ss}`;
}

export function Call() {
  const nav = useNavigate();
  const { id: callId = '' } = useParams();
  const [sp] = useSearchParams();
  const fromParam = sp.get('from') ?? undefined;
  const incoming = useStore((s) => s.incomingCall);
  const setIncoming = useStore((s) => s.setIncomingCall);
  const setActive = useStore((s) => s.setActiveCall);
  const contacts = useStore((s) => s.contacts);

  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);
  const [speakerOn, setSpeakerOn] = useState(true);
  const [state, setState] = useState<'ringing' | 'connecting' | 'connected' | 'ended'>('connecting');
  const [connectedAt, setConnectedAt] = useState<number | null>(null);

  const localVid = useRef<HTMLVideoElement>(null);
  const remoteVid = useRef<HTMLVideoElement>(null);

  // Try to resolve peer for display (from incoming or from active handle's peer if available)
  const peer = useMemo(() => {
    const peerId = incoming?.from ?? fromParam;
    return peerId ? contacts.find((c) => c.id === peerId) : undefined;
  }, [incoming, fromParam, contacts]);

  const elapsed = useElapsed(connectedAt);

  useEffect(() => {
    let handle = getActive(callId);

    async function accept() {
      if (!incoming || incoming.callId !== callId) return;
      const h = await answerCall(callId, incoming.from, incoming.sdp);
      setActive(callId);
      setIncoming(null);
      handle = h;
      attach();
    }

    function attach() {
      const h = getActive(callId);
      if (!h) return;
      if (localVid.current) {
        localVid.current.srcObject = h.localStream;
        localVid.current.play().catch(() => {});
      }
      if (remoteVid.current) {
        remoteVid.current.srcObject = h.remoteStream;
        remoteVid.current.play().catch(() => {});
      }
      h.remoteStream.onaddtrack = () => { remoteVid.current?.play().catch(() => {}); };
      h.pc.onconnectionstatechange = () => {
        const s = h.pc.connectionState;
        if (s === 'connected') { setState('connected'); setConnectedAt((v) => v ?? Date.now()); }
        else if (s === 'failed' || s === 'closed' || s === 'disconnected') setState('ended');
      };
    }

    if (handle) {
      attach();
      setState('connecting');
    } else if (incoming && incoming.callId === callId) {
      setState('ringing');
      accept();
    } else if (fromParam) {
      setState('ringing');
    } else {
      nav('/');
    }
  }, [callId, incoming, fromParam, nav, setActive, setIncoming]);

  useEffect(() => {
    if (state !== 'ended') return;
    const t = setTimeout(() => nav('/'), 800);
    return () => clearTimeout(t);
  }, [state, nav]);

  function hangup() {
    const h = getActive(callId);
    if (h) h.hangup();
    else wsClient.send({ type: 'call.end', callId, reason: 'hangup' });
    endCall(callId);
    setActive(null);
    setState('ended');
  }
  function toggleMic() {
    const h = getActive(callId);
    if (!h) return;
    const next = !micOn;
    h.toggleMic(next);
    setMicOn(next);
  }
  function toggleCam() {
    const h = getActive(callId);
    if (!h) return;
    const next = !camOn;
    h.toggleCam(next);
    setCamOn(next);
  }

  const statusText = state === 'connected' ? `Connected · ${elapsed}` :
                     state === 'connecting' ? 'Connecting…' :
                     state === 'ringing' ? 'Ringing…' :
                     'Call ended';

  return (
    <div className="call">
      <video ref={remoteVid} className="remote" autoPlay playsInline />
      <video ref={localVid} className="local" autoPlay playsInline muted />

      <div className="call-topbar">
        <div className={`avatar sm`} data-tone={peer ? toneFor(peer.id) : 'clay'} style={{ width: 32, height: 32, fontSize: 13 }}>
          {peer ? initials(peer.displayName) : '·'}
        </div>
        <div className="who-text" style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span className="name">{peer?.displayName ?? 'Call'}</span>
          <span className="status">{statusText}</span>
        </div>
        <button aria-label="Minimize" style={{ background: 'transparent', border: 'none', color: 'var(--ink-700)', padding: 4, display: 'flex' }}>
          <MinimizeIcon />
        </button>
      </div>

      {state === 'connected' && (
        <div style={{ position: 'absolute', left: 'var(--s-4)', top: 'calc(env(safe-area-inset-top) + 160px)', zIndex: 22, display: 'flex', alignItems: 'center', gap: 8, color: 'rgba(250,245,238,0.85)', fontSize: 11, fontWeight: 600 }}>
          <SignalIcon />
          <span>HD · steady</span>
        </div>
      )}

      <div className="call-controls">
        <button className={`icon-btn ${micOn ? '' : 'off'}`} onClick={toggleMic} aria-label="Mute">
          {micOn ? <MicOnIcon /> : <MicOffIcon />}
        </button>
        <button className={`icon-btn ${camOn ? '' : 'off'}`} onClick={toggleCam} aria-label="Camera">
          {camOn ? <CamOnIcon /> : <CamOffIcon />}
        </button>
        <button className={`icon-btn ${speakerOn ? '' : 'off'}`} onClick={() => setSpeakerOn((v) => !v)} aria-label="Speaker">
          <SpeakerIcon />
        </button>
        <button className="icon-btn hangup" onClick={hangup} aria-label="End">
          <HangupIcon />
        </button>
      </div>
    </div>
  );
}
